import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shopApi } from '../utils/api';
import { ShoppingCart, Package, Plus, Minus, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';

interface Category {
  id: number;
  name: string;
  description?: string;
}

interface ShopItem {
  id: number;
  name: string;
  price: number;
  stock: number;
  description?: string;
}

interface CartItem extends ShopItem {
  quantity: number;
}

export default function Shop() {
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);

  const { data: categories } = useQuery({
    queryKey: ['shopCategories'],
    queryFn: async () => {
      const response = await shopApi.getCategories();
      return response.data.categories as Category[];
    },
  });

  const { data: items, isLoading } = useQuery({
    queryKey: ['shopItems', selectedCategory],
    queryFn: async () => {
      const response = await shopApi.getItems(selectedCategory || undefined);
      return response.data.items as ShopItem[];
    },
  });

  const orderMutation = useMutation({
    mutationFn: () => shopApi.createOrder({
      items: cart.map((item) => ({ itemId: item.id, quantity: item.quantity })),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopItems'] });
      toast.success('訂單已提交！');
      setCart([]);
      setShowCart(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || '下單失敗');
    },
  });

  const addToCart = (item: ShopItem) => {
    const existing = cart.find((c) => c.id === item.id);
    if (existing) {
      if (existing.quantity >= item.stock) {
        toast.error('庫存不足');
        return;
      }
      setCart(cart.map((c) => (c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c)));
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
    toast.success(`已加入 ${item.name}`);
  };

  const updateQuantity = (id: number, delta: number) => {
    setCart(cart.map((c) => {
      if (c.id === id) {
        const newQty = c.quantity + delta;
        if (newQty <= 0) return null;
        if (newQty > c.stock) {
          toast.error('庫存不足');
          return c;
        }
        return { ...c, quantity: newQty };
      }
      return c;
    }).filter(Boolean) as CartItem[]);
  };

  const removeFromCart = (id: number) => {
    setCart(cart.filter((c) => c.id !== id));
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('購物車是空的');
      return;
    }
    orderMutation.mutate();
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg">
            <ShoppingCart className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">販賣部</h1>
            <p className="text-slate-500">班級販賣部購物中心</p>
          </div>
        </div>
        <button onClick={() => setShowCart(!showCart)} className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-medium transition-all shadow-lg relative">
          <ShoppingCart className="h-5 w-5" />
          購物車
          {cart.length > 0 && (
            <span className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {cart.length}
            </span>
          )}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        <button onClick={() => setSelectedCategory(null)} className={`px-4 py-2 rounded-xl font-medium transition-all ${selectedCategory === null ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'}`}>
          全部商品
        </button>
        {categories?.map((cat) => (
          <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`px-4 py-2 rounded-xl font-medium transition-all ${selectedCategory === cat.id ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'}`}>
            {cat.name}
          </button>
        ))}
      </div>

      {showCart && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCart(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-rose-50 rounded-lg flex items-center justify-center">
                    <ShoppingCart className="h-5 w-5 text-rose-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">購物車</h3>
                </div>
                <button onClick={() => setShowCart(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="h-5 w-5 text-slate-400" />
                </button>
              </div>
            </div>
            <div className="p-6 max-h-96 overflow-y-auto">
              {cart.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">購物車是空的</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cart.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <p className="font-semibold text-slate-900">{item.name}</p>
                        <p className="text-sm text-slate-500">${item.price.toLocaleString()} x {item.quantity}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQuantity(item.id, -1)} className="w-8 h-8 flex items-center justify-center bg-white rounded-lg shadow-sm"><Minus className="h-4 w-4" /></button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.id, 1)} className="w-8 h-8 flex items-center justify-center bg-white rounded-lg shadow-sm"><Plus className="h-4 w-4" /></button>
                        <button onClick={() => removeFromCart(item.id)} className="w-8 h-8 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-lg ml-2"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {cart.length > 0 && (
              <div className="p-6 border-t border-slate-100 bg-slate-50">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-600">總計</span>
                  <span className="text-2xl font-bold text-slate-900">${cartTotal.toLocaleString()}</span>
                </div>
                <button onClick={handleCheckout} disabled={orderMutation.isPending} className="w-full py-3 bg-gradient-to-r from-rose-500 to-pink-600 text-white font-semibold rounded-xl shadow-lg disabled:opacity-50">
                  {orderMutation.isPending ? '處理中...' : '結帳'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-slate-500">載入商品中...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items?.map((item) => (
            <div key={item.id} className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="h-40 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                <Package className="h-16 w-16 text-slate-300" />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-slate-900 mb-1">{item.name}</h3>
                {item.description && <p className="text-sm text-slate-500 mb-3 line-clamp-2">{item.description}</p>}
                <div className="flex items-center justify-between mt-4">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">${item.price.toLocaleString()}</p>
                    <p className={`text-sm ${item.stock > 10 ? 'text-emerald-600' : item.stock > 0 ? 'text-amber-600' : 'text-rose-600'}`}>
                      {item.stock > 0 ? `庫存: ${item.stock}` : '暫時缺貨'}
                    </p>
                  </div>
                  <button onClick={() => addToCart(item)} disabled={item.stock === 0} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white font-medium rounded-xl transition-all disabled:cursor-not-allowed">
                    {item.stock === 0 ? '缺貨' : '加入'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
